<template>
  <div class="message-notification">
    <a-badge :count="unreadCount" :offset="[10, 0]">
      <a-button 
        type="text" 
        :icon="h(BellOutlined)" 
        @click="showMessageCenter"
        class="notification-button"
      />
    </a-badge>
    
    <!-- 消息中心抽屉 -->
    <a-drawer
      v-model:open="drawerVisible"
      title="消息中心"
      placement="right"
      width="600"
      :closable="true"
      @close="handleDrawerClose"
    >
      <div class="message-center-drawer">
        <!-- 快速消息列表 -->
        <div class="quick-messages">
          <h4>最近消息</h4>
          <a-list
            :data-source="recentMessages.slice(0, 10)"
            :loading="loading"
            size="small"
          >
            <template #renderItem="{ item }">
              <a-list-item
                :class="{ 'unread': !item.is_read }"
                @click="handleMessageClick(item)"
              >
                <a-list-item-meta>
                  <template #title>
                    <div class="message-title">
                      <span class="sender-name">
                        {{ item.sender?.name || '系统' }}
                      </span>
                      <span class="message-time">
                        {{ formatTime(item.created_at) }}
                      </span>
                    </div>
                  </template>
                  <template #description>
                    <div class="message-preview" :title="item.translated_content || item.original_content">
                      {{ getMessagePreview(item) }}
                    </div>
                  </template>
                </a-list-item-meta>
                <template #actions>
                  <a-button
                    v-if="!item.is_read"
                    type="link"
                    size="small"
                    @click.stop="markAsRead(item)"
                  >
                    标记已读
                  </a-button>
                </template>
              </a-list-item>
            </template>
          </a-list>
        </div>
        
        <!-- 操作按钮 -->
        <div class="drawer-actions">
          <a-space>
            <a-button @click="goToMessageCenter">
              打开完整消息中心
            </a-button>
            <a-button
              v-if="hasUnreadMessages"
              type="primary"
              @click="markAllAsRead"
            >
              全部标记已读
            </a-button>
            <a-button @click="refreshMessages" :loading="loading">
              刷新消息
            </a-button>
          </a-space>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, h } from 'vue'
import { useRouter } from 'vue-router'
import { BellOutlined } from '@ant-design/icons-vue'
import { 
  getMessages, 
  getUnreadCount, 
  markMessageAsRead, 
  markMessagesAsRead,
  type MessageResponse 
} from '../api'
import { getRelativeTime } from '@/shared/utils'
import { useDrawerManager } from '@/composables/useDrawerManager'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'

const router = useRouter()
const drawerManager = useDrawerManager()

// 动态导入 message API，避免模块加载时的 "Cannot access 'mu' before initialization" 错误
const showToast = async (type: 'success' | 'error' | 'info', content: string) => {
  const { default: msg } = await import('ant-design-vue/es/message')
  msg[type](content)
}

// 抽屉框ID
const DRAWER_ID = 'message-notification-drawer'

// 响应式数据
const drawerVisible = ref(false)
const loading = ref(false)
const unreadCount = ref(0)
const recentMessages = ref<MessageResponse[]>([])

// 轮询相关：与 research/upload 页一致，可见时短间隔同步角标，不可见时拉长节省请求。
// 请求压力：可见时约 4 次/分钟/用户（仅 GET /messages/unread-count），不可见时 1 次/分钟；若需进一步降低可改为 30s。
const POLLING_INTERVAL_ACTIVE_MS = 15000   // 15 秒（标签页可见时）
const POLLING_INTERVAL_HIDDEN_MS = 60000   // 60 秒（标签页不可见时）
let pollingTimer: ReturnType<typeof setInterval> | null = null

// 计算属性
const hasUnreadMessages = computed(() => {
  return recentMessages.value.some(msg => !msg.is_read)
})

// 显示消息中心
const showMessageCenter = () => {
  // 通知抽屉框管理器打开此抽屉框（会自动关闭其他抽屉框）
  if (drawerManager) {
    drawerManager.openDrawer(DRAWER_ID)
  }
  drawerVisible.value = true
  // 加载最近消息
  loadRecentMessages()
}

// 处理抽屉关闭
const handleDrawerClose = () => {
  drawerVisible.value = false
}

// 加载最近消息
const loadRecentMessages = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  try {
    if (manageLoading) {
      loading.value = true
    }
    const response = await getMessages({
      page: 1,
      page_size: 20
    }, batchCtx?.requestConfig)
    recentMessages.value = response.items
  } catch (error) {
    if (!batchCtx) {
      console.error('加载最近消息失败:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loading.value = false
    }
  }
}

const loadUnreadCount = async (batchCtx?: BatchContext) => {
  try {
    const response = await getUnreadCount(batchCtx?.requestConfig)
    unreadCount.value = response.unread_count
  } catch (error) {
    if (!batchCtx) {
      console.error('加载未读消息数量失败:', error)
    }
    throw error
  }
}

const refreshMessages = async () => {
  await runApiBatch([
    (ctx) => loadRecentMessages(ctx),
    (ctx) => loadUnreadCount(ctx),
  ])
}

// 开始轮询（支持按可见性切换间隔）
const startPolling = (intervalMs: number = POLLING_INTERVAL_ACTIVE_MS) => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
  pollingTimer = setInterval(() => {
    loadUnreadCount()
  }, intervalMs)
}

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

// 根据页面可见性切换轮询间隔，与 research/upload 页策略一致
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    startPolling(POLLING_INTERVAL_ACTIVE_MS)
  } else {
    startPolling(POLLING_INTERVAL_HIDDEN_MS)
  }
}

// 处理消息点击
const handleMessageClick = async (message: MessageResponse) => {
  // 标记为已读
  if (!message.is_read) {
    await markAsRead(message)
  }
  
  // 跳转到消息中心
  goToMessageCenter()
}

// 标记单个消息为已读
const markAsRead = async (message: MessageResponse) => {
  try {
    await markMessageAsRead(message.id)
    
    // 更新本地状态
    const messageIndex = recentMessages.value.findIndex(msg => msg.id === message.id)
    if (messageIndex !== -1) {
      recentMessages.value[messageIndex].is_read = true
    }
    
    // 更新未读计数
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    
    await showToast('success', '已标记为已读')
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

// 标记所有消息为已读
const markAllAsRead = async () => {
  try {
    const unreadMessages = recentMessages.value.filter(msg => !msg.is_read)
    if (unreadMessages.length === 0) {
      await showToast('info', '没有未读消息')
      return
    }
    
    await markMessagesAsRead(unreadMessages.map(msg => msg.id))
    
    // 更新本地状态
    unreadMessages.forEach(msg => {
      const messageIndex = recentMessages.value.findIndex(m => m.id === msg.id)
      if (messageIndex !== -1) {
        recentMessages.value[messageIndex].is_read = true
      }
    })
    
    // 清空未读计数
    unreadCount.value = 0
    
    await showToast('success', `已标记 ${unreadMessages.length} 条消息为已读`)
  } catch (error) {
    console.error('批量标记已读失败:', error)
  }
}

// 跳转到消息中心页面
const goToMessageCenter = () => {
  drawerVisible.value = false
  router.push('/profile/messages')
}

// 获取消息预览
const getMessagePreview = (message: MessageResponse): string => {
  const content = message.translated_content || message.original_content
  // 增加预览长度到150个字符，保留更多信息
  // 对于长消息，在换行符处截断会更美观
  if (content.length <= 150) {
    return content
  }
  
  // 尝试在第一个换行符之后150字符内截断
  const firstNewline = content.indexOf('\n', 100)
  if (firstNewline > 0 && firstNewline < 150) {
    return content.substring(0, firstNewline) + '...'
  }
  
  // 如果前150字符内没有换行符，直接截断
  return content.substring(0, 150) + '...'
}

// 格式化时间
const formatTime = (dateString: string): string => {
  return getRelativeTime(dateString)
}

// 注册抽屉框关闭回调
const closeDrawerCallback = () => {
  drawerVisible.value = false
}

// 组件挂载
onMounted(async () => {
  // 注册抽屉框到管理器
  if (drawerManager) {
    drawerManager.registerDrawer(DRAWER_ID, closeDrawerCallback)
  }
  
  // 加载初始数据
  await loadUnreadCount()
  
  // 按当前可见性开始轮询（可见 15s，不可见 60s），与 research/upload 页角标同步
  handleVisibilityChange()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

// 组件卸载
onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (drawerManager) {
    drawerManager.unregisterDrawer(DRAWER_ID)
  }
  stopPolling()
})

// 暴露方法给父组件
defineExpose({
  refreshUnreadCount: loadUnreadCount
})
</script>

<style scoped>
.message-notification {
  display: inline-block;
}

.notification-button {
  font-size: 16px;
  color: #666;
  transition: color 0.2s;
  
  &:hover {
    color: #1890ff;
  }
}

:deep(.ant-badge-count) {
  font-size: 12px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
}

/* 抽屉内容样式 */
.message-center-drawer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.quick-messages {
  flex: 1;
  margin-bottom: 16px;
  
  h4 {
    margin-bottom: 16px;
    color: #333;
  }
}

.message-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sender-name {
  font-weight: 500;
  color: #333;
}

.message-time {
  font-size: 12px;
  color: #999;
}

.message-preview {
  color: #666;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap; /* 保留换行符 */
  word-break: break-word; /* 长单词自动换行 */
  max-height: 120px; /* 限制最大高度 */
  overflow: hidden; /* 超出部分隐藏 */
  display: -webkit-box;
  -webkit-line-clamp: 4; /* 显示最多4行 */
  line-clamp: 4; /* 标准属性，兼容性 */
  -webkit-box-orient: vertical;
}

:deep(.ant-list-item.unread) {
  background-color: #f6ffed;
  border-left: 3px solid #52c41a;
  padding-left: 13px;
}

.drawer-actions {
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}

/* 移除连接状态相关样式 */
</style>

